import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_3e66b0ed-93a2-4dc7-abca-ce9b0d15b317/artifacts/su7kwk53_StartEase%20Logo.png';

const OTPVerifyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  const email = location.state?.email;
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(60);
  const [resendLoading, setResendLoading] = useState(false);
  
  const inputRefs = useRef([]);

  // Redirect if no email
  useEffect(() => {
    if (!email) {
      navigate('/login');
    }
  }, [email, navigate]);

  // Resend cooldown timer - starts at 60
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
      setOtp(newOtp);
      inputRefs.current[Math.min(pastedData.length, 5)]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        email,
        otp_code: otpCode,
      });
      
      const { token, user } = response.data;
      console.log('Login response user:', user);
      console.log('User role:', user?.role);
      login(token, user);
      
      // Redirect based on user role
      if (user?.role === 'super_admin' || user?.role === 'admin') {
        console.log('Redirecting to /superadmin, role:', user?.role);
        navigate('/superadmin');
      } else {
        console.log('Redirecting to /dashboard, role was:', user?.role);
        navigate('/dashboard');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Invalid or expired code. Try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    
    setResendLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/auth/send-otp`, { email });
      setResendCooldown(60);
      setOtp(['', '', '', '', '', '']);
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleDifferentEmail = () => {
    navigate('/login');
  };

  if (!email) return null;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden" style={{ backgroundColor: '#0b1310' }}>
      {/* Subtle background pattern */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #059669 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}
      />
      
      {/* Gradient overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(16, 185, 129, 0.08) 0%, transparent 50%)'
        }}
      />

      <div className="w-full max-w-[420px] relative z-10">
        {/* Logo & Branding */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src={LOGO_URL} 
              alt="StartEase Agent" 
              className="w-16 h-16"
            />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">StartEase Agent</h1>
          <p className="text-gray-500 text-sm mt-1">{"US entity, tax & compliance — done for you"}</p>
        </div>

        {/* OTP Card */}
        <div 
          className="rounded-xl p-10"
          style={{ 
            backgroundColor: '#111827',
            border: '1px solid #1f2937',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }}
        >
          <h2 className="text-2xl font-semibold text-white mb-2">Check your email</h2>
          <p className="text-gray-400 text-sm mb-8">
            We sent a 6-digit code to{' '}
            <span className="text-white font-medium">{email}</span>
          </p>

          <form onSubmit={handleVerify}>
            {/* OTP Input Boxes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-4">
                Enter verification code
              </label>
              <div className="flex gap-2 sm:gap-3 justify-center" onPaste={handlePaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 sm:w-[48px] sm:h-[56px] text-center text-xl font-bold rounded-lg text-white transition-all duration-200 focus:outline-none"
                    style={{ 
                      backgroundColor: '#0b1310',
                      border: digit ? '1px solid #059669' : '1px solid #1f2937',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#059669';
                      e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.15)';
                    }}
                    onBlur={(e) => {
                      if (!digit) e.target.style.borderColor = '#1f2937';
                      e.target.style.boxShadow = 'none';
                    }}
                    disabled={loading}
                    data-testid={`otp-input-${index}`}
                  />
                ))}
              </div>
              {error && (
                <p className="mt-4 text-sm text-red-400 text-center" data-testid="error-message">
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-4 rounded-lg font-semibold text-black transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 active:scale-[0.98]"
              style={{ backgroundColor: '#059669' }}
              data-testid="verify-otp-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Verifying...
                </span>
              ) : (
                'Verify Code'
              )}
            </button>
          </form>

          {/* Resend & Different Email Links */}
          <div className="mt-8 text-center space-y-3">
            <button
              onClick={handleResend}
              disabled={resendCooldown > 0 || resendLoading}
              className="text-sm transition-colors disabled:cursor-not-allowed"
              style={{ 
                color: resendCooldown > 0 ? '#6B7280' : '#059669'
              }}
              data-testid="resend-btn"
            >
              {resendLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </span>
              ) : resendCooldown > 0 ? (
                `Resend code in ${resendCooldown}s`
              ) : (
                <span className="hover:underline">Resend code</span>
              )}
            </button>
            
            <div className="border-t border-gray-800 pt-4 mt-4">
              <button
                onClick={handleDifferentEmail}
                className="text-sm text-gray-400 hover:text-white transition-colors"
                data-testid="different-email-btn"
              >
                Use a different email
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-8">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="text-[#059669] hover:underline">Terms</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-[#059669] hover:underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
};

export default OTPVerifyPage;
